import _ from 'lodash';
import React from 'react';
import { createPluginFactory } from '@udecode/plate';
import { Editor, Node, Point, Range, Text, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import AgentMention from './agent_mention';
import {
  AGENT_MENTION,
  AGENT_MENTION_ACTIVATION_CHARACTER,
  MARK_AGENT_MENTION_SEARCH,
} from './agent_mention_constants';
import createUndoHistoryBreak from 'components/text_editor_new/lib/create_undo_history_break';
import ensureFullSelection from 'components/text_editor_new/lib/ensure_full_selection';
import getNodes from 'components/text_editor_new/lib/get_nodes';
import removeInlineFromStartOfSelection from 'components/text_editor_new/lib/remove_inline_from_start_of_selection';
import removeInlineOnBackspace from 'components/text_editor_new/lib/remove_inline_on_backspace';

// Ports the old `text_editor/plugins/agent_mention` plugin to the new Plate/Slate editor. Agents type
// `@` to start a search, the typed text is tracked via the MARK_AGENT_MENTION_SEARCH leaf mark, and
// selecting an agent from the menu replaces that text with an atomic AGENT_MENTION inline element.
//
// `menuActionsRef` is shared with the AgentMentionMenu: the menu registers onArrowUp/onArrowDown/onInsert
// on it so this plugin's onKeyDown can drive the menu's selection without hoisting state into the editor.
// Returns BOTH the element plugin and the leaf-mark plugin; callers spread them into the plugin list.
export default function createAgentMentionPlugin(menuActionsRef) {
  const elementPlugin = createPluginFactory({
    key: AGENT_MENTION,
    isElement: true,
    isInline: true,

    component: props => <AgentMention {...props} />,

    withOverrides: editor => withAgentMentions(editor, menuActionsRef),

    handlers: {
      // The editor keeps its Slate selection on blur, so onChange's selection-moved-out check won't fire.
      // Clear any in-progress search explicitly, otherwise a half-typed "@search" lingers and pops the menu
      // back open when focus returns. Mirrors the old text_editor/plugins/agent_mention onBlur.
      onBlur: editor => () => {
        if (getAgentMentionText(editor) !== null) {
          removeAgentMentionSearch(editor);
        }
      },

      onChange: editor => () => {
        // Treat completed mentions as atomic: a click inside selects the whole thing.
        ensureFullSelection(editor, n => n.type === AGENT_MENTION);
        // If the cursor moved out of an in-progress search, abandon it.
        removeAgentMentionSearchIfSelectionMovedOutside(editor);
      },

      // If we have a completed mention selected (via ensureFullSelection) and start typing, replace
      // the whole mention rather than editing its text.
      onDOMBeforeInput: editor => evt => {
        if (removeInlineFromStartOfSelection(editor, evt, AGENT_MENTION)) {
          return true;
        }
        // A collapsed cursor can resolve to the inside edge of a mention; typing there would edit the
        // chip's text (and sometimes double the character). Mentions are atomic, so push the insert out.
        return insertTextOutsideMention(editor, evt);
      },

      onKeyDown: editor => evt => {
        onKeyDown(editor, menuActionsRef, evt);
      },
    },

    // NOTE: these serializeHtml/deserializeHtml hooks are vestigial — they are never invoked. Agent-mention
    // persistence runs through the hand-rolled lib/serialize_html.js and lib/deserialize_html.js switches
    // (the <agentmention>/AGENTMENTION cases). Kept only for parity with the sibling inline plugins
    // (variables/placeholders/inline_links), which carry the same unused config.
    deserializeHtml: {
      getNode: el => ({
        attributes: { 'data-agentid': el.getAttribute('data-agentid') },
        type: AGENT_MENTION,
      }),
      rules: [{ validNodeName: ['AGENTMENTION'] }],
    },

    serializeHtml: props => {
      const { children, element } = props;
      return <agentmention data-agentid={element.attributes?.['data-agentid']}>{children}</agentmention>;
    },
  })();

  const searchMarkPlugin = createPluginFactory({
    key: MARK_AGENT_MENTION_SEARCH,
    isLeaf: true,
  })();

  return [elementPlugin, searchMarkPlugin];
}

// editor command: insert the activation character has already happened; mark it and arm the search so
// subsequently typed characters inherit the search mark.
export function startAgentMentionSearch(editor) {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return;
  }

  const end = Range.end(selection);
  if (end.offset === 0) {
    return;
  }

  // Select the just-inserted "@" and mark it. Adding the mark over a range marks that text; collapsing
  // back to the end leaves the mark "pending" so the next typed character inherits it too.
  //
  // These are real, structure-changing operations (marking splits the text into a separate leaf), so we
  // must let them go into the undo history. Hiding them with HistoryEditor.withoutSaving desyncs the
  // history — recorded ops end up referencing leaf paths that the unrecorded split/merge has since
  // invalidated, which crashes undo with "Cannot find a descendant at path ...".
  const atRange = {
    anchor: { path: end.path, offset: end.offset - 1 },
    focus: end,
  };
  Transforms.select(editor, atRange);
  Editor.addMark(editor, MARK_AGENT_MENTION_SEARCH, true);
  Transforms.collapse(editor, { edge: 'end' });
}

// editor command: stop the current search and strip the search mark from any text it covers.
export function removeAgentMentionSearch(editor) {
  // Unsetting the mark merges the search leaf back into its neighbours — a structural change that must be
  // recorded in history (see startAgentMentionSearch). withoutNormalizing only batches the cleanup; it
  // does NOT hide the ops from undo.
  Editor.withoutNormalizing(editor, () => {
    // Clear the pending mark so the next typed character is not marked.
    Editor.removeMark(editor, MARK_AGENT_MENTION_SEARCH);

    for (const [, path] of getNodes(editor, {
      at: [],
      match: n => Text.isText(n) && n[MARK_AGENT_MENTION_SEARCH],
    })) {
      Transforms.unsetNodes(editor, MARK_AGENT_MENTION_SEARCH, { at: path });
    }
  });
}

// editor query: for an active search "@mar", return "mar". Returns null when no search is active.
export function getAgentMentionText(editor) {
  let text = '';
  for (const [node] of getNodes(editor, {
    at: [],
    match: n => Text.isText(n) && n[MARK_AGENT_MENTION_SEARCH],
  })) {
    text += node.text;
  }

  if (!text || text[0] !== AGENT_MENTION_ACTIVATION_CHARACTER) {
    return null;
  }
  return text.slice(1);
}

// editor query: a deduplicated list of all agent ids currently mentioned in the editor.
export function getMentionedAgentIds(editor) {
  const agentIds = [];
  for (const [node] of getNodes(editor, { at: [], match: n => n.type === AGENT_MENTION })) {
    const agentId = node.attributes?.['data-agentid'];
    if (agentId) {
      agentIds.push(agentId);
    }
  }
  return _.uniq(agentIds);
}

// editor command: replace the active "@search" text with a completed mention for the chosen agent,
// followed by a trailing space so the agent can keep typing.
export function completeAgentMentionSearch(editor, menuActionsRef, { email, id, name }) {
  const searchText = getAgentMentionText(editor);
  if (searchText == null) {
    return;
  }

  // Start a fresh undo batch so completing the mention is its own history step: a single Cmd-Z undoes
  // just the mention insertion (dropping back to the "@search") rather than also eating the characters
  // the agent typed beforehand. Mirrors the autolink plugin's createUndoHistoryBreak before it wraps a URL.
  createUndoHistoryBreak(editor);

  removeAgentMentionSearch(editor);

  // Delete the "@" plus the typed search text, leaving the cursor where the mention should go.
  Transforms.delete(editor, { unit: 'character', distance: searchText.length + 1, reverse: true });

  // Insert the chip and the trailing space as siblings in one shot. Inserting the space as its own text
  // node (rather than a separate insertText after insertNodes) keeps it OUTSIDE the atomic mention —
  // otherwise insertNodes leaves the cursor inside the inline and the space ends up within the chip.
  Transforms.insertNodes(editor, [
    {
      type: AGENT_MENTION,
      attributes: { 'data-agentid': id },
      children: [{ text: `${AGENT_MENTION_ACTIVATION_CHARACTER}${name || email}` }],
    },
    { text: ' ' },
  ]);

  ReactEditor.focus(editor);

  if (menuActionsRef.current) {
    menuActionsRef.current.hasJustInsertedMention = true;
  }
}

function withAgentMentions(editor, menuActionsRef) {
  const { normalizeNode } = editor;
  editor.normalizeNode = ([node, path]) => {
    // A mention with no text is meaningless (e.g. the agent deleted its way through it), so drop the
    // whole node. Mirrors the inline-link and variable plugins.
    if (node?.type === AGENT_MENTION && !Node.string(node)) {
      Transforms.removeNodes(editor, { at: path });
      return;
    }
    normalizeNode([node, path]);
  };

  editor.startAgentMentionSearch = () => startAgentMentionSearch(editor);
  editor.removeAgentMentionSearch = () => removeAgentMentionSearch(editor);
  editor.getAgentMentionText = () => getAgentMentionText(editor);
  editor.getMentionedAgentIds = () => getMentionedAgentIds(editor);
  editor.completeAgentMentionSearch = agent => completeAgentMentionSearch(editor, menuActionsRef, agent);
  return editor;
}

function onKeyDown(editor, menuActionsRef, evt) {
  // Composing usually means an IME (e.g. a Japanese keyboard) is mid-input.
  if (ReactEditor.isComposing(editor)) {
    return;
  }

  // After inserting a mention we auto-add a trailing space. If the agent immediately types a space or
  // punctuation that shouldn't be preceded by whitespace, drop that extra space.
  if (menuActionsRef.current?.hasJustInsertedMention && isInsertingKey(evt.key)) {
    menuActionsRef.current.hasJustInsertedMention = false;
    if (isPunctuationOrSpace(evt.key) && hasWhitespaceImmediatelyBefore(editor)) {
      Transforms.delete(editor, { unit: 'character', reverse: true });
    }
  }

  // If the cursor is in/around a completed mention, backspace removes the whole thing.
  removeInlineOnBackspace(editor, evt, AGENT_MENTION);
  if (evt.defaultPrevented) {
    return;
  }

  const hasActiveSearch = getAgentMentionText(editor) !== null;

  // Start a new search on "@" (only at the start of a line or after whitespace).
  if (evt.key === AGENT_MENTION_ACTIVATION_CHARACTER && !hasActiveSearch) {
    if (!isAtLineStartOrAfterWhitespace(editor)) {
      return;
    }
    evt.preventDefault();
    Transforms.insertText(editor, AGENT_MENTION_ACTIVATION_CHARACTER);
    editor.startAgentMentionSearch();
    return;
  }

  if (evt.key === 'Escape' && hasActiveSearch) {
    editor.removeAgentMentionSearch();
    return;
  }

  if (!hasActiveSearch) {
    return;
  }

  // A space (or a second space) cancels the search.
  if (evt.key === ' ') {
    const searchText = getAgentMentionText(editor);
    if (!searchText || searchText.indexOf(' ') > -1) {
      editor.removeAgentMentionSearch();
      return;
    }
  }

  // The menu owns which item is selected, so delegate navigation/selection keys to it.
  if (evt.key === 'Enter' || evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === 'Tab') {
    if (evt.key === 'Enter' || evt.key === 'Tab') {
      const inserted = menuActionsRef.current?.onInsert?.();
      if (inserted) {
        evt.preventDefault();
      } else {
        editor.removeAgentMentionSearch();
      }
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      menuActionsRef.current?.onArrowUp?.();
    } else if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      menuActionsRef.current?.onArrowDown?.();
    }
    return;
  }

  // Backspacing away the last character (the "@") ends the search.
  if (evt.key === 'Backspace' && getAgentMentionText(editor) === '') {
    editor.removeAgentMentionSearch();
  }
}

// When the collapsed cursor sits on the inside edge of a completed mention, redirect a typed character
// to just outside the chip (after the trailing edge, before the leading edge). A mention is atomic, so
// typing should never land within it. Returns true if it handled the insert.
function insertTextOutsideMention(editor, evt) {
  if (evt.inputType !== 'insertText' || evt.data == null) {
    return false;
  }

  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return false;
  }

  const point = Range.start(selection);
  const mentionEntry = Editor.above(editor, { at: point, match: n => n.type === AGENT_MENTION });
  if (!mentionEntry) {
    return false;
  }

  const [, mentionPath] = mentionEntry;
  const isAtEnd = Point.equals(point, Editor.end(editor, mentionPath));
  const isAtStart = Point.equals(point, Editor.start(editor, mentionPath));
  if (!isAtEnd && !isAtStart) {
    return false;
  }

  const outside = isAtEnd ? Editor.after(editor, mentionPath) : Editor.before(editor, mentionPath);
  if (!outside) {
    return false;
  }

  evt.preventDefault();
  Transforms.select(editor, outside);
  Transforms.insertText(editor, evt.data);
  return true;
}

function removeAgentMentionSearchIfSelectionMovedOutside(editor) {
  const marked = getNodes(editor, {
    at: [],
    match: n => Text.isText(n) && n[MARK_AGENT_MENTION_SEARCH],
  });
  if (!marked.length) {
    return;
  }

  const { selection } = editor;
  if (!selection) {
    editor.removeAgentMentionSearch();
    return;
  }

  const searchStart = Editor.start(editor, marked[0][1]);
  const searchEnd = Editor.end(editor, _.last(marked)[1]);
  const point = Range.end(selection);

  // The cursor is allowed to sit anywhere within the marked text (the natural place to keep typing is
  // right at its end). Anything outside means the agent navigated away, so abandon the search.
  const isWithinSearch = Point.compare(point, searchStart) >= 0 && Point.compare(point, searchEnd) <= 0;
  if (!isWithinSearch) {
    editor.removeAgentMentionSearch();
  }
}

function isAtLineStartOrAfterWhitespace(editor) {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return true;
  }

  const start = Range.start(selection);

  // Don't short-circuit on `start.offset === 0`: that's only the start of the current text leaf, which at
  // a mark or inline-element split sits mid-line (e.g. right after a bold run or another mention). Let
  // Editor.before find the previous character — it returns undefined only at the true block start.
  const before = Editor.before(editor, start, { unit: 'character' });
  if (!before) {
    return true;
  }

  const charBefore = Editor.string(editor, { anchor: before, focus: start });
  return /\s/.test(charBefore);
}

function hasWhitespaceImmediatelyBefore(editor) {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return false;
  }

  const start = Range.start(selection);
  const before = Editor.before(editor, start, { unit: 'character' });
  if (!before) {
    return false;
  }

  return Editor.string(editor, { anchor: before, focus: start }) === ' ';
}

function isInsertingKey(key) {
  return key !== 'Control' && key !== 'Shift';
}

function isPunctuationOrSpace(key) {
  return /[.,:;!?' ]/g.test(key);
}
